/**
 * AtlasCandlestickChart — lightweight-charts v5 OHLC + volume panel.
 *
 * Two-series setup:
 *   main: candlestick series (big panel)
 *   sub:  volume histogram (bottom 22%) — colour-matched to candle direction
 *
 * Props:
 *   candles  = [{ time, open, high, low, close, volume }, ...]  (unix seconds)
 *   height   = total chart height in px (default 420)
 */
import React, { useEffect, useRef } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
} from 'lightweight-charts';

const UP = '#4ade80';
const DOWN = '#f87171';

export default function AtlasCandlestickChart({
  candles = [],
  height = 420,
  testId = 'atlas-candlestick',
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);

  // Create chart once
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      localization: { locale: 'en-US' },
      layout: {
        background: { color: 'transparent' },
        textColor: 'rgba(210, 220, 240, 0.8)',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(90, 110, 140, 0.12)' },
        horzLines: { color: 'rgba(90, 110, 140, 0.12)' },
      },
      rightPriceScale: {
        borderColor: 'rgba(90, 110, 140, 0.3)',
        scaleMargins: { top: 0.08, bottom: 0.25 },
      },
      timeScale: {
        borderColor: 'rgba(90, 110, 140, 0.3)',
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: { mode: 1 },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      borderUpColor: UP,
      borderDownColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      color: '#60a5fa50',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      if (containerRef.current && chart) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [height]);

  // Sync data
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    const cs = (candles || []).map((c) => ({
      time: c.time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    const vs = (candles || []).map((c) => ({
      time: c.time,
      value: c.volume,
      color: c.close >= c.open ? `${UP}60` : `${DOWN}60`,
    }));
    candleSeriesRef.current.setData(cs);
    volumeSeriesRef.current.setData(vs);
    if (chartRef.current && cs.length > 0) {
      chartRef.current.timeScale().fitContent();
    }
  }, [candles]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      style={{ width: '100%', height, position: 'relative' }}
    />
  );
}
